import {
	Button,
	Group,
	LoadingOverlay,
	Modal,
	Space,
	Stepper,
} from '@mantine/core';
import {
	IconAddressBook,
	IconChevronLeft,
	IconChevronRight,
	IconCircleCheck,
	IconEmergencyBed,
	IconPassword,
	IconUser,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SmallModal } from '../../../components/SmallModal';
import { useModal } from '../../../hooks/useModal';
import { mutate } from '../../../util/api/mutate';
import { ButtonOrCancel } from '../../vat-reports/components/VatReturnExportModal';
import { useAddStaffAddressForm } from '../hooks/useAddStaffAddressForm';
import { useAddStaffEmergencyForm } from '../hooks/useAddStaffEmergencyForm';
import { useAddStaffGeneralForm } from '../hooks/useAddStaffGeneralForm';
import { useAddStaffPasswordForm } from '../hooks/useAddStaffPasswordForm';
import type { FullOperatorDto } from '../types/OperatorDto';
import {
	UpsertStaffAddress,
	UpsertStaffEmergencyContact,
	UpsertStaffGeneralInfo,
	UpsertStaffPassword,
} from './UpsertStaffMemberComponents';
import { Title } from '../../../components/Title';

interface AddStaffMemberModalProps {
	opened: boolean;
	close: () => void;
	onAddComplete: () => void;
}

const defaultOperator: FullOperatorDto = {
	id: '',
	username: '',
	cardNo: '',
	firstName: '',
	surname: '',
	jobTitle: '',
	branch: '',
	postCode: '',
	town: '',
	county: '',
	addressLineOne: '',
	addressLineTwo: '',
	phone: '',
	email: '',
	emergencyContactName: '',
	emergencyContactNumber: '',
	notes: '',
	branchId: '',
	password: '',
	checkPassword: '',
};

export function NewStaffMemberModal({
	opened,
	close,
	onAddComplete,
}: AddStaffMemberModalProps) {
	const { organisation } = useParams();

	const [selectedStep, setSelectedStep] = useState(0);
	const [finalStaffMember, setFinalStaffMember] = useState(defaultOperator);
	const [shouldMutate, setShouldMutate] = useState(false);

	const generalForm = useAddStaffGeneralForm(defaultOperator);
	const addressForm = useAddStaffAddressForm(defaultOperator);
	const emergencyForm = useAddStaffEmergencyForm(defaultOperator);
	const passwordForm = useAddStaffPasswordForm();

	const usernameTakenModal = useModal();
	const successModal = useModal();

	const closeAndReset = () => {
		successModal.close();
		setSelectedStep(0);
		setFinalStaffMember(defaultOperator);

		generalForm.form.reset();
		addressForm.form.reset();
		emergencyForm.form.reset();
		passwordForm.form.reset();

		close();
	};

	const addOperatorMutation = mutate<FullOperatorDto>(
		`${organisation}/operators/add`,
		finalStaffMember,
	);

	const complete = () => {
		usernameTakenModal.close();
		successModal.close();
		closeAndReset();
		onAddComplete();
	};

	useEffect(() => {
		const performEdit = async () => {
			await addOperatorMutation.mutateAsync().then((response) => {
				if (response.isValid) {
					successModal.open();
				} else {
					response.validationMessages.forEach((v) => {
						if (v.inputIdentifier === 'Username') {
							usernameTakenModal.open();
						}
					});

					addOperatorMutation.reset();
					setShouldMutate(false);
				}
			});
		};

		if (shouldMutate) {
			performEdit();
		}
	}, [finalStaffMember, shouldMutate]);

	return (
		<>
			<LoadingOverlay
				visible={addOperatorMutation.isLoading}
				loaderProps={{ color: 'medepos.0', type: 'dots' }}
			/>
			<Modal
				opened={opened}
				onClose={close}
				title={<Title label={'Add Staff Member'} />}			w={100}
				withCloseButton={false}
				size={'lg'}
			>
				<SmallModal
					isOpen={usernameTakenModal.isOpen}
					close={() => {
						setSelectedStep(0);
						usernameTakenModal.close();
					}}
					title={'Validation Error'}
					body={`Username ${generalForm.inputs.username} is taken. Click Ok to continue...`}
				/>
				<SmallModal
					isOpen={successModal.isOpen}
					close={successModal.close}
					title={'Staff Member Added!'}
					body={
						'Staff member has been successfully added. Click Ok to continue...'
					}
					onComplete={complete}
				/>
				<Stepper
					active={selectedStep}
					completedIcon={<IconCircleCheck />}
					color={'medepos.0'}
					iconSize={25}
				>
					<Stepper.Step
						icon={<IconUser />}
						description={'General'}
						allowStepSelect={false}
					>
						<form
							onSubmit={generalForm.form.onSubmit(() => {
								generalForm.form.validate();
							})}
						>
							<UpsertStaffGeneralInfo form={generalForm.form} />
							<Space h={20} />
							<ButtonOrCancel
								closeAndReset={() => {
									setFinalStaffMember(defaultOperator);
									closeAndReset();
								}}
								onClick={() => {
									if (generalForm.form.isValid()) {
										setFinalStaffMember((prevInputs) => ({
											...prevInputs,
											username: generalForm.inputs.username,
											cardNo: generalForm.inputs.cardNo,
											firstName: generalForm.inputs.firstName,
											surname: generalForm.inputs.surname,
											jobTitle: generalForm.inputs.jobTitle,
											phone: generalForm.inputs.phone,
											email: generalForm.inputs.email,
											branchId: generalForm.inputs.branchId,
										}));
										setSelectedStep(1);
									}
								}}
								onGoBack={() => setSelectedStep(0)}
							/>
						</form>
					</Stepper.Step>
					<Stepper.Step
						icon={<IconAddressBook />}
						description={'Address'}
						allowStepSelect={false}
					>
						<form
							onSubmit={addressForm.form.onSubmit(() => {
								addressForm.form.validate();
							})}
						>
							<UpsertStaffAddress form={addressForm.form} />
							<Space h={20} />

							<ButtonOrCancel
								onGoBack={() => setSelectedStep(0)}
								onClick={() => {
									if (addressForm.form.isValid()) {
										setFinalStaffMember((prevInputs) => ({
											...prevInputs,
											postCode: addressForm.inputs.postCode,
											town: addressForm.inputs.town,
											county: addressForm.inputs.county,
											addressLineOne: addressForm.inputs.addressLineOne,
											addressLineTwo: addressForm.inputs.addressLineTwo,
										}));
										setSelectedStep(2);
									}
								}}
							/>
						</form>
					</Stepper.Step>
					<Stepper.Step
						icon={<IconEmergencyBed />}
						description={'Emergency'}
						allowStepSelect={false}
					>
						<form
							onSubmit={emergencyForm.form.onSubmit(() => {
								emergencyForm.form.validate();
							})}
						>
							<UpsertStaffEmergencyContact form={emergencyForm.form} />
							<Space h={20} />

							<ButtonOrCancel
								onGoBack={() => setSelectedStep(1)}
								onClick={() => {
									if (emergencyForm.form.isValid()) {
										setFinalStaffMember((prevInputs) => ({
											...prevInputs,
											emergencyContactName:
												emergencyForm.inputs.emergencyContactName,
											emergencyContactNumber:
												emergencyForm.inputs.emergencyContactNumber,
										}));
										setSelectedStep(3);
									}
								}}
							/>
						</form>
					</Stepper.Step>
					<Stepper.Step
						icon={<IconPassword />}
						description={'Password'}
						allowStepSelect={false}
					>
						<form onSubmit={passwordForm.form.onSubmit(() => {})}>
							<UpsertStaffPassword form={passwordForm.form} />
							<Space h={20} />
							<Group justify={'flex-end'}>
								<Button
									color={'medepos.1'}
									variant={'filled'}
									leftSection={<IconChevronLeft />}
									onClick={() => setSelectedStep(2)}
									data-cy="go-back-button"
								>
									Go Back
								</Button>
								<Button
									color={'medepos.1'}
									variant={'filled'}
									rightSection={<IconChevronRight />}
									onClick={() => {
										if (passwordForm.form.isValid()) {
											setFinalStaffMember((prevInputs) => ({
												...prevInputs,
												password: passwordForm.inputs.password,
												checkPassword: passwordForm.inputs.checkPassword,
											}));
											setShouldMutate(true);
										}
									}}
									type={'submit'}
									data-cy="complete-button"
								>
									Complete
								</Button>
							</Group>
						</form>
					</Stepper.Step>
				</Stepper>
			</Modal>
		</>
	);
}
